import { useMutation, useQueryClient, type QueryClient, type UseMutationResult } from "@tanstack/react-query";
import { create } from "zustand";
import { apiClient } from "../api/client";
import { amendRefusalLine } from "../lib/reviewLines";
import { isRunInFlight, useGlobalRunStore } from "./useGlobalRunStore";
import { isRunInFlightRefusal, useRunInFlightRefusal, type RunInFlightRefusal } from "./useRunInFlightRefusal";

// The amend of a confirmed decision (0143's endpoint; 0148's covered-by row,
// 0051 slice 3's spec drawer): ONE hook, so an amend from either surface
// follows one rule - the refusal before posting, the POST, its invalidations
// and the in-flight rule. The POST names no carrier: the server mints the
// default one. The withdraw of a waiting change (useWithdrawChange, 0160)
// lives here too, on the same rule.

// AmendRefusedError is a refused or failed amend, carrying the error
// envelope's code (undefined for a body that is not an envelope or a network
// failure); its message is the founder's line for that code (amendRefusalLine).
// An amend.run_in_flight refusal is read by its code through the shared
// in-flight refusal rule (hooks/useRunInFlightRefusal, 0159 F1).
export class AmendRefusedError extends Error {
  readonly code: string | undefined;
  constructor(code: string | undefined) {
    super(amendRefusalLine(code));
    this.code = code;
  }
}

export type AmendVariables = { decisionId: string; answerText: string };

// 0160: a second change the server refused because one to the decision already
// waits (409 amend.same_unserved), recorded per (product, decision) OUTSIDE the
// surface that posted it. The 409 can land after that surface unmounted - the
// founder closed the spec drawer, pressed Escape or opened another row while
// the save was pending - and a refusal kept in the surface's own state would
// land on nothing: reopened, the decision would show only the saved line, and
// the founder would take their refused text for the change that waits. The
// surface reads the record on its next mount of that decision
// (useAmendRefusedAsWaiting) and clears it once it has shown it
// (clearAmendRefusedAsWaiting). Only a surface that shows the waiting change on
// this refusal (one that passes onAlreadyWaiting) has a record made.
const refusalKey = (productId: string, decisionId: string) => JSON.stringify([productId, decisionId]);
const useRefusedAsWaitingStore = create<{ refused: Record<string, true> }>()(() => ({ refused: {} }));

// useAmendRefusedAsWaiting is whether a refused second change to this
// product's decision is recorded and not yet cleared.
export function useAmendRefusedAsWaiting(productId: string, decisionId: string): boolean {
  const key = refusalKey(productId, decisionId);
  return useRefusedAsWaitingStore((s) => s.refused[key] === true);
}

// clearAmendRefusedAsWaiting drops the record, once the surface has shown it.
export function clearAmendRefusedAsWaiting(productId: string, decisionId: string): void {
  const key = refusalKey(productId, decisionId);
  useRefusedAsWaitingStore.setState((s) => {
    if (s.refused[key] !== true) return s;
    const refused = { ...s.refused };
    delete refused[key];
    return { refused };
  });
}

function recordRefusedAsWaiting(productId: string, decisionId: string): void {
  const key = refusalKey(productId, decisionId);
  useRefusedAsWaitingStore.setState((s) => ({ refused: { ...s.refused, [key]: true } }));
}

// refreshAmendReads asks again the reads a change to a decision moves: the
// questions queue, the product's tree key (which covers the spec read) and the
// products list. The amend asks them on a save and on a refusal that says a
// change already waits; the withdraw (useWithdrawChange) asks them too.
function refreshAmendReads(queryClient: QueryClient, productId: string): void {
  queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "questions"] });
  queryClient.invalidateQueries({ queryKey: ["product", productId, "tree"] });
  queryClient.invalidateQueries({ queryKey: ["products"] });
}

// refuseBeforePosting is THE REFUSAL BEFORE POSTING, the one the amend and the
// withdraw both run: while this page tracks a run for the product, post
// nothing (the run's next stage commit would erase the act). The server's own
// check reads the product's live run lease (0159); it stays the authority for
// a run this page does not track, and for one the store still tracks after it
// ended (a stream still reconnecting): this refusal, like the server's, asks
// the products list again, and once an answer newer than it shows no active
// run for the product (the shared rule's serverShowsNoRun) this check stands
// aside and the next act posts - the server accepts it or answers its own 409.
function refuseBeforePosting(productId: string, inFlight: Pick<RunInFlightRefusal, "serverShowsNoRun">): void {
  if (isRunInFlight(useGlobalRunStore.getState().runs, productId) && !inFlight.serverShowsNoRun) {
    throw new AmendRefusedError("amend.run_in_flight");
  }
}

// postAmendRoute sends one POST of the amend routes (the amend, the withdraw)
// and decodes its failure, the one decode both read: a network failure is an
// AmendRefusedError with no code, an error body one carrying the envelope's
// code (undefined for a body that is not an envelope).
async function postAmendRoute(post: () => Promise<{ error?: unknown }>): Promise<void> {
  let error: unknown;
  try {
    ({ error } = await post());
  } catch {
    throw new AmendRefusedError(undefined);
  }
  if (error) {
    const code = (error as { error?: { code?: string } }).error?.code;
    throw new AmendRefusedError(code);
  }
}

export function useAmendDecision(
  productId: string,
  opts: {
    // The surface's own outcome on a 2xx (clear its draft, show the saved
    // line), run before the invalidations.
    onSaved: () => void;
    // Whether the surface shows its own in-flight signal (the shared rule's
    // option): the spec drawer's disabled Amend and its line do; the covered-by
    // row has none.
    ownInFlightSignal: boolean;
    // OPTIONAL (0160): passed by a surface that shows the waiting change on an
    // amend.same_unserved refusal - a change to this decision already waits,
    // saved from a page this one's read predates. The hook then records the
    // refusal for the decision (useAmendRefusedAsWaiting), so the surface
    // shows it before the refetch lands and on its next mount if it unmounted
    // while the POST was pending, and runs this, the surface's own outcome
    // (the spec drawer drops the refused text); both before the invalidations.
    onAlreadyWaiting?: () => void;
  },
): { amend: UseMutationResult<void, Error, AmendVariables>; errorLine: string | null } {
  // A35: the mid-run signal, read from the app's global run store itself - it
  // refreshes with no fetch of its own and survives navigation onto this page.
  const runInFlight = useGlobalRunStore((s) => isRunInFlight(s.runs, productId));
  const queryClient = useQueryClient();
  // 0159 F1: an in-flight refusal - the refusal before posting, or the
  // server's 409 amend.run_in_flight for a run this page does not track -
  // goes through the shared rule, so its line shows only until the server's
  // answer about the product's run changes, and the server's refusal asks the
  // products list again (discovery then follows the run). The covered-by row
  // has no other in-flight signal, so the line also shows while the run is in
  // flight there.
  const amendInFlight = useRunInFlightRefusal(productId, runInFlight, {
    line: amendRefusalLine("amend.run_in_flight"),
    ownInFlightSignal: opts.ownInFlightSignal,
  });
  const amend = useMutation({
    mutationFn: async ({ decisionId, answerText }: AmendVariables) => {
      // THE REFUSAL BEFORE POSTING (refuseBeforePosting): while this page
      // tracks a run for the product, post nothing.
      refuseBeforePosting(productId, amendInFlight);
      await postAmendRoute(() =>
        apiClient.POST("/api/v1/products/{pid}/decisions/{did}/amend", {
          // client.ts already sends X-Requested-With and credentials on every
          // request; the typed client still requires the declared header
          // param, as on every other mutating call.
          params: {
            path: { pid: productId, did: decisionId },
            header: { "X-Requested-With": "xhr" },
          },
          body: { answer_text: answerText },
        }),
      );
    },
    // The amend's side effects live HERE, in the options, never in a callback
    // passed to mutate(): TanStack Query runs mutate()-level callbacks only
    // while the observer still has listeners, so a surface unmounted before the
    // POST settles (a Cmd/Ctrl+Arrow while pending) would skip them and leave
    // the queue stale; the options-level onSuccess runs from the mutation.
    onSuccess: () => {
      // The text now lives on the server as the carrier's answer; the surface
      // collapses in place to its saved line.
      opts.onSaved();
      refreshAmendReads(queryClient, productId);
    },
    // An in-flight refusal is taken by the shared rule; any other failure
    // keeps its founder copy (the error's message) until the next amend.
    // amend.same_unserved (0160): the server refused a second change because
    // one to this decision already waits, which this page's reads predate - so
    // the refusal is recorded for the decision the POST named (here, so it
    // holds when the surface unmounted while the POST was pending), the
    // surface runs its own outcome (onAlreadyWaiting), and the reads that show
    // the waiting change are asked again, as on a save.
    onError: (err, { decisionId }) => {
      amendInFlight.note(err);
      if (err instanceof AmendRefusedError && err.code === "amend.same_unserved") {
        if (opts.onAlreadyWaiting !== undefined) {
          recordRefusedAsWaiting(productId, decisionId);
          opts.onAlreadyWaiting();
        }
        refreshAmendReads(queryClient, productId);
      }
    },
  });

  // The amend's failure line: an in-flight refusal reads the shared rule's
  // line (null once the server's answer about the run has changed), any other
  // failure its founder copy.
  const errorLine =
    amend.error == null
      ? null
      : isRunInFlightRefusal(amend.error)
        ? amendInFlight.line
        : amend.error.message;

  return { amend, errorLine };
}

export type WithdrawVariables = { decisionId: string; carrierQuestionId: string };

// useWithdrawChange is the founder's withdraw of a waiting change (0160,
// founder ruling 2026-10-20; the spec drawer's WITHDRAW_CHANGE_ACT): the way
// out of a change the founder no longer wants, and the app's way to replace
// one (withdraw, then amend again). The POST names the carrier the founder saw
// the change on, so an older read never withdraws a newer change waiting on
// another carrier. It follows the amend's rule: the refusal before posting and
// the server's amend.run_in_flight go through the same shared in-flight rule.
// On a 2xx it runs opts.onWithdrawn, then asks again the amend's reads
// (refreshAmendReads) and the decisions queue (a withdrawn replacement leaves
// it). A 409 amend.not_waiting (nothing waits on that carrier any more: it
// landed, was withdrawn elsewhere, or a newer change waits) shows no line: the
// same reads are asked again and the surface shows what is true. Any other
// failure's line is amendRefusalLine's for its code.
export function useWithdrawChange(
  productId: string,
  opts: {
    // The surface's own outcome on a 2xx (the drawer's after-line and its
    // prefilled Amend), run before the invalidations.
    onWithdrawn: () => void;
    // Whether the surface shows its own in-flight signal (the shared rule's
    // option): the spec drawer's disabled link and its line do.
    ownInFlightSignal: boolean;
  },
): { withdraw: UseMutationResult<void, Error, WithdrawVariables>; errorLine: string | null } {
  const runInFlight = useGlobalRunStore((s) => isRunInFlight(s.runs, productId));
  const queryClient = useQueryClient();
  const withdrawInFlight = useRunInFlightRefusal(productId, runInFlight, {
    line: amendRefusalLine("amend.run_in_flight"),
    ownInFlightSignal: opts.ownInFlightSignal,
  });
  const refreshWithdrawReads = () => {
    refreshAmendReads(queryClient, productId);
    queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "decisions"] });
  };
  const withdraw = useMutation({
    mutationFn: async ({ decisionId, carrierQuestionId }: WithdrawVariables) => {
      // THE REFUSAL BEFORE POSTING, the amend's (refuseBeforePosting).
      refuseBeforePosting(productId, withdrawInFlight);
      await postAmendRoute(() =>
        apiClient.POST("/api/v1/products/{pid}/decisions/{did}/amend/withdraw", {
          params: {
            path: { pid: productId, did: decisionId },
            header: { "X-Requested-With": "xhr" },
          },
          body: { carrier_question_id: carrierQuestionId },
        }),
      );
    },
    // In the options, never in mutate(): a surface unmounted before the POST
    // settles still has its reads asked again (useAmendDecision's reason).
    onSuccess: () => {
      opts.onWithdrawn();
      refreshWithdrawReads();
    },
    onError: (err) => {
      withdrawInFlight.note(err);
      if (err instanceof AmendRefusedError && err.code === "amend.not_waiting") refreshWithdrawReads();
    },
  });

  const errorLine =
    withdraw.error == null
      ? null
      : isRunInFlightRefusal(withdraw.error)
        ? withdrawInFlight.line
        : withdraw.error instanceof AmendRefusedError && withdraw.error.code === "amend.not_waiting"
          ? null
          : withdraw.error.message;

  return { withdraw, errorLine };
}
